import { pageNameToConfigIndex } from '@/helper/pageOrder';
import type { TemplateConfigState } from '@/reducers/TemplateConfig.types';

export const DEMOGRAPHIC_INFO_PAGE_TITLE = 'Demographic Info';

const DECLARATIONS_PAGE_NAME = 'declarations';
const DECLARATION_LABEL = /declaration/i;
const DEMOGRAPHIC_LABEL = /demographic/i;

function hasIncludedDeclarationQuestions(templateConfig?: Partial<TemplateConfigState>): boolean {
  const declarationsConfig = templateConfig?.pages?.declarations;
  return !!(
    declarationsConfig?.form1003DeclarationQuestions?.some((question) => question.includeInQuestions) ||
    declarationsConfig?.generalDeclarationQuestions?.some((question) => question.includeInQuestions)
  );
}

function isDeclarationsPageName(page?: string): boolean {
  return page?.trim().toLowerCase() === DECLARATIONS_PAGE_NAME;
}

/**
 * Whether the lender's `pages` array carries a declarations slot.
 *
 * `pages` is positional: indexes into it (see `pageNameToConfigIndex`) only shift
 * when the slot is absent from the array. Hiding the step does not remove the slot,
 * so this is NOT the same question as `isDeclarationsVisible`.
 */
export function isDeclarationsInConfigPages(templateConfig?: Partial<TemplateConfigState>): boolean {
  return templateConfig?.pages?.pages?.some(isDeclarationsPageName) ?? false;
}

/**
 * Single source of truth for whether the Declarations step is shown.
 *
 * Callers (router, sidebar, HMDA "next page") must agree: if the router hides the
 * route but a caller still points at it, navigation falls through to the wildcard
 * redirect and lands back on Let's Get Started.
 *
 * Visible only when the page is listed in the config AND, when `isEnabled` is
 * present, it is true AND at least one question is included.
 */
export function isDeclarationsVisible(templateConfig?: Partial<TemplateConfigState>): boolean {
  if (!isDeclarationsInConfigPages(templateConfig)) return false;

  const declarationsConfig = templateConfig?.pages?.declarations;
  if (declarationsConfig?.isEnabled === false) return false;

  if (declarationsConfig?.isEnabled === true) {
    return hasIncludedDeclarationQuestions(templateConfig);
  }

  if (templateConfig?.isICE === true) {
    return hasIncludedDeclarationQuestions(templateConfig);
  }

  return true;
}

function resolvePageTitle(configuredName: string | undefined, pageName: string, templateConfig?: Partial<TemplateConfigState>, configIndex?: number): string {
  const label = configuredName || pageName;
  const gmiConfigIndex = pageNameToConfigIndex['Demographic Information'];
  const declarationsConfigIndex = pageNameToConfigIndex.Declarations;
  const isGmiPage = pageName === 'Demographic Information' || configIndex === gmiConfigIndex;
  const isDeclarationsPage = pageName === 'Declarations' || configIndex === declarationsConfigIndex;

  if (isGmiPage && !isDeclarationsPage) {
    if (templateConfig?.isICE === true) {
      return DEMOGRAPHIC_INFO_PAGE_TITLE;
    }
    if (!isDeclarationsVisible(templateConfig) && DECLARATION_LABEL.test(label)) {
      const demographicFromConfig = templateConfig?.pages?.pages?.find((page) => DEMOGRAPHIC_LABEL.test(page));
      return demographicFromConfig || DEMOGRAPHIC_INFO_PAGE_TITLE;
    }
    return label;
  }

  if (isDeclarationsPage) {
    if (!isDeclarationsVisible(templateConfig)) {
      return label;
    }
    const slotTitle = templateConfig?.pages?.pages?.find(isDeclarationsPageName);
    if (configuredName && isDeclarationsPageName(configuredName)) {
      return configuredName;
    }
    if (configuredName && !isDeclarationsPageName(configuredName) && (DEMOGRAPHIC_LABEL.test(configuredName) || /government/i.test(configuredName))) {
      return slotTitle || pageName;
    }
    return slotTitle || label;
  }

  if (isDeclarationsVisible(templateConfig)) {
    return label;
  }

  const isDemographicPage = DEMOGRAPHIC_LABEL.test(pageName);
  if (!isDemographicPage || !DECLARATION_LABEL.test(label)) {
    return label;
  }

  const demographicFromConfig = templateConfig?.pages?.pages?.find((page) => DEMOGRAPHIC_LABEL.test(page));
  return demographicFromConfig || DEMOGRAPHIC_INFO_PAGE_TITLE;
}

export function getConfiguredPageTitle(templateConfig: Partial<TemplateConfigState> | undefined, configIndex: number | undefined, pageName: string): string {
  const configuredName = configIndex !== undefined ? templateConfig?.pages?.pages?.[configIndex] : undefined;
  return resolvePageTitle(configuredName, pageName, templateConfig, configIndex);
}
